import React, { Fragment, useContext, useEffect } from 'react';

import { PageContext } from '../../context/PageContext';
import SuggestionsContextProvider from '../../context/SuggestionsContext.jsx';

import { EVENTS } from '../../utilities/CONSTANTS';
import { getApiSluggedPageUrlWithToken } from '../../utilities/urlHelper';
import getParamsFromSearchURL from '../../utilities/getParamsFromSearchURL';
import { updateWindowTitle } from '../../utilities/documentTitleUtilities';
import { removeMessage } from '../../utilities/notificationUtilities';
import { trackPageAnalytics } from '../../utilities/analyticsUtilities';
import { scrollToHeader } from '../../utilities/scrollUtilities';

import PageHeader from './PageHeader';
import PageTitle from './PageTitle';
import PageBody from './PageBody';
import PageTOC from './PageTOC';
import PageError from './PageError';
import PageNotification from './PageNotification';
import ExistingDraftNotification from './ExistingDraftNotification';
import NeedsVerificationNotification from './NeedsVerificationNotification';
import PageGeneratedTagsContainer from './PageGeneratedTagsContainer';
import PageViewsContainer from './PageViewsContainer';
import PageThankYousContainer from './PageThankYousContainer';
import CommentsAndSuggestionsContainer from '../CommentsAndSuggestionsContainer';
import SuggestUpdateWrapper from './SuggestPageUpdateWrapper';
import { canShowVerification } from '../../utilities/permissionsUtilities';
import PageGeneratedQuestionsContainer from './PageGeneratedQuestionsContainer';

const PAGE_ERROR = -1;

let isFirstLoad = true;

const getApiPageSlugUrl = (slug, searchUrl) => {
    const params = getParamsFromSearchURL(searchUrl);
    const token = params.auth;
    return getApiSluggedPageUrlWithToken(slug, token);
};

const setWindowVariables = (page) => {
    const {
        category_id, parents, folder_id, title, id,
    } = page || {};
    window.categoryId = category_id;
    window.breadcrumbs = parents.map(subcategory => subcategory.id);
    window.folderId = folder_id;
    window.pageTitle = title;
    window.pageId = id;
    window.page = page;
    window._tettraDraft = null;
    window.draftId = null;
};

const afterPageLoad = (page, { state }) => {
    updateWindowTitle(page.title);
    setWindowVariables(page);
    const { title, context } = { ...state };
    trackPageAnalytics(title, context);
    window.dispatchEvent(new Event(EVENTS.NEW_PAGE_LOADED));
    scrollToHeader();
    if (isFirstLoad) isFirstLoad = false;
};

const beforePageLoad = (props) => {
    if (isFirstLoad) return;
    removeMessage();
    updateWindowTitle('Loading...');
};

let activeRequest = null;

const PageContainer = ({ page }) => {
    const hideFooterClass = (page && (page.editor_state || page.html)) ? '' : 'hide';
    return (
        <Fragment>
            {isFirstLoad && <PageNotification />}
            <ExistingDraftNotification />
            {canShowVerification && <NeedsVerificationNotification />}
            <PageHeader />
            <div className="flex overflow-hidden">
                <PageTOC />
                <div className="page-content-container">
                    <PageTitle />
                    <div className="page-middle-width">
                        <PageTOC />
                    </div>
                    <PageBody />
                    {window.loggedIn && (
                        <div className={`page-middle-width mt1 mb3 print-hide ${hideFooterClass}`}>
                            <div className="flex flex-wrap">
                                <PageViewsContainer page={page} />
                                <PageGeneratedTagsContainer page={page} />
                                <PageGeneratedQuestionsContainer page={page} />
                                <PageThankYousContainer page={page} />
                                <SuggestUpdateWrapper page={page} />
                            </div>
                            <CommentsAndSuggestionsContainer page={page} />
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

const PageShowContainer = (props) => {
    const { page, setPage } = useContext(PageContext);

    useEffect(() => {
        const title = (({ state }) => {
            const { title } = { ...state };
            return isFirstLoad ? window.pageTitle : title;
        })(props.location);
        setPage({ title });
        beforePageLoad(props);

        if (activeRequest) {
            activeRequest.abort();
        }

        activeRequest = $.get(getApiPageSlugUrl(props.pageSlug, window.location.search));
        activeRequest
            .then(({ success, page }) => {
                if (success) {
                    setPage(page);
                    afterPageLoad(page, props.location);
                } else {
                    setPage(PAGE_ERROR);
                }
            }).fail(({ statusText, ...error }) => {
                if (statusText && !statusText.includes('abort')) {
                    setPage(PAGE_ERROR);
                    console.error(error);
                }
            });
    }, [props.pageSlug]);

    if (page === PAGE_ERROR) {
        return <PageError />;
    }

    return <PageContainer page={page} />;
};

const PageShowContainerWithContext = (props) => (
    <SuggestionsContextProvider>
        <PageShowContainer {...props} />
    </SuggestionsContextProvider>
);

export default PageShowContainerWithContext;
